import { auth } from './firebase';

const GA_ENDPOINT = 'https://www.google-analytics.com/mp/collect';

// NOTE: You will need to add these two variables to your .env files
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const GA_API_SECRET = import.meta.env.VITE_GA_API_SECRET;

// Retrieves or generates a persistent client ID
async function getClientId() {
  // If running inside the Chrome Extension
  if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local) {
    const result = await chrome.storage.local.get('ga_client_id');
    let clientId = result.ga_client_id;
    if (!clientId) {
      clientId = crypto.randomUUID();
      await chrome.storage.local.set({ ga_client_id: clientId });
    }
    return clientId;
  }
  
  // Fallback for Web/Desktop environments
  let clientId = localStorage.getItem('ga_client_id');
  if (!clientId) {
    clientId = crypto.randomUUID();
    localStorage.setItem('ga_client_id', clientId);
  }
  return clientId;
}

/**
 * Sends a custom event to Google Analytics via the Measurement Protocol.
 * This is 100% compliant with Chrome Extension MV3 (No Remotely Hosted Code).
 * 
 * @param {string} eventName - The name of the event (e.g., 'extension_opened', 'snippet_saved')
 * @param {object} params - Optional custom parameters for the event
 */
export async function trackEvent(eventName, params = {}) {
  // If native gtag is available (Web / Desktop), use it natively!
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
    console.debug(`[Analytics (Native)] Event tracked: ${eventName}`);
    return;
  }

  // Extension Fallback: Use Measurement Protocol (Avoids RHC violations)
  if (!GA_MEASUREMENT_ID || !GA_API_SECRET) {
    console.warn('Google Analytics Measurement ID or API Secret is missing. Event not sent via Measurement Protocol.');
    return;
  }

  try {
    const clientId = await getClientId();
    // If the user is signed in to Firebase, link the event to their user ID
    const userId = auth?.currentUser?.uid || undefined;

    const payload = {
      client_id: clientId,
      ...(userId && { user_id: userId }), // Only include user_id if authenticated
      events: [
        {
          name: eventName,
          params: {
            ...params,
            // A hardcoded session ID is often used in extensions because traditional web sessions 
            // don't map perfectly to extension background worker lifecycles.
            session_id: '1', 
          },
        },
      ],
    };

    await fetch(`${GA_ENDPOINT}?measurement_id=${GA_MEASUREMENT_ID}&api_secret=${GA_API_SECRET}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    console.debug(`[Analytics] Event tracked: ${eventName}`);
  } catch (error) {
    console.error('[Analytics] Failed to send event to Google Analytics:', error);
  }
}
